import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { ChevronRight, PhotoCameraOutlined, FitnessCenter } from '@mui/icons-material';
import UserAvatar from 'components/shared/user/UserAvatar';
import { PLAY_SELF_REPORTED_RESULTS } from 'api/graphql/play';
import { metricLabel, muscleLabel } from 'components/play/constants';
import routes from 'common/routes';

const MAX_RESULTS = 200;

/** Una marca de tiempo se lee en mm:ss; el resto es un número plano. */
const formatValue = (metric, value) => {
  if (metric !== 'TIME_SECONDS') return String(value);
  const total = Math.round(value);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Marcas de OTRIX Play en el panel de Rendimientos del admin. Es una lista y no la tabla de
 * `dynamicPerformances` porque el dato es de otra naturaleza: lo auto-reporta el cliente (con
 * foto) o lo deriva su entrenamiento, y lo que importa de un vistazo es quién, qué y cuánto.
 */
function PlayResultsList({ nameFilter = '', exerciseFilter = 'all' }) {
  const { t, i18n } = useTranslation(['play', 'performances', 'labels']);
  const navigate = useNavigate();

  const { data, loading } = useQuery(PLAY_SELF_REPORTED_RESULTS, {
    variables: { first: MAX_RESULTS },
    fetchPolicy: 'cache-and-network'
  });

  const results = useMemo(() => {
    const rows = data?.playSelfReportedResults || [];
    const query = nameFilter.trim().toLowerCase();

    return rows.filter((row) => {
      if (exerciseFilter !== 'all' && row.exercise?.localized_name !== exerciseFilter) return false;
      if (query && !(row.user?.full_name || '').toLowerCase().includes(query)) return false;
      return true;
    });
  }, [data, nameFilter, exerciseFilter]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(i18n.language === 'es' ? 'es-CO' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

  if (loading && results.length === 0) {
    return (
      <Stack spacing={1.25}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: '14px' }} />
        ))}
      </Stack>
    );
  }

  if (results.length === 0) {
    return (
      <Box
        sx={{
          py: 8,
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'hairline.main',
          borderRadius: 3,
          color: 'text.secondary'
        }}>
        <FitnessCenter sx={{ fontSize: 44, mb: 1 }} />
        <Typography variant="body1" fontWeight={700}>
          {t('adminResults.emptyTitle', { ns: 'play', defaultValue: 'Todavía no hay marcas de Play' })}
        </Typography>
        <Typography variant="body2">
          {t('adminResults.emptyBody', {
            ns: 'play',
            defaultValue: 'Aparecerán aquí en cuanto un cliente registre una marca o termine un entrenamiento.'
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {results.map((row) => {
        const openClient = () =>
          navigate(routes.viewClientPerformance.path.replace(':id', row.user?.id));

        return (
          <Stack
            key={row.id}
            direction="row"
            alignItems="center"
            spacing={1.5}
            role="button"
            tabIndex={0}
            onClick={openClient}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openClient();
              }
            }}
            sx={{
              p: 1.75,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.content',
              cursor: 'pointer',
              transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms',
              '&:hover': { backgroundColor: 'surface.hover', transform: 'translateY(-2px)' },
              '&:active': { transform: 'scale(0.98)' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
            }}>
            <UserAvatar border size={40} textSize={17} user={row.user || {}} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>
                {row.user?.full_name}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                  {row.exercise?.localized_name}
                </Typography>
                {row.exercise?.muscle_group && (
                  <Chip
                    size="small"
                    label={muscleLabel(t, row.exercise.muscle_group)}
                    sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'gold.soft', color: 'gold.main' }}
                  />
                )}
                {/* Manual = el cliente subió foto; entrenamiento = lo derivó el motor de series. */}
                <Chip
                  size="small"
                  variant="outlined"
                  icon={row.source === 'manual' ? <PhotoCameraOutlined sx={{ fontSize: 12 }} /> : undefined}
                  label={
                    row.source === 'manual'
                      ? t('adminResults.manual', { ns: 'play', defaultValue: 'Con foto' })
                      : t('adminResults.fromWorkout', { ns: 'play', defaultValue: 'Del entrenamiento' })
                  }
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
                {row.is_flagged && (
                  <Chip
                    size="small"
                    label={t('adminResults.flagged', { ns: 'play', defaultValue: 'Por revisar' })}
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      bgcolor: 'sessionStatus.pending.bg',
                      color: 'sessionStatus.pending.fg'
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Box sx={{ textAlign: 'right', flex: 'none' }}>
              <Typography sx={{ color: 'gold.main', fontWeight: 800, lineHeight: 1.1 }}>
                {formatValue(row.metric, row.value)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {metricLabel(t, row.metric)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {formatDate(row.created_at)}
              </Typography>
            </Box>

            <ChevronRight sx={{ color: 'text.disabled' }} />
          </Stack>
        );
      })}
    </Stack>
  );
}

PlayResultsList.propTypes = {
  nameFilter: PropTypes.string,
  exerciseFilter: PropTypes.string
};

export default PlayResultsList;
